import React, { useState } from 'react'
import { Bar, BarChart, CartesianGrid, Label, Legend, Tooltip, XAxis, YAxis, ResponsiveContainer } from 'recharts'
import PropTypes from 'prop-types'

import { chartMargins, legendStyling } from '../../../../utils/renderUtils'
import { colors, occupancyDataKeySuffix, percentageTickFormatter } from '../../../../utils/kpiUtils'
import OccupancyTooltip from './occupancyTooltip/OccupancyTooltip'
import CustomLegend from '../customLegend/CustomLegend'

const OccupancyBarChart = props => {
  // contains data types to be filtered from chart
  const [disabledStations, setDisabledStations] = useState([])

  /**
   * Removes data type from chart
   * @param dataKey Recharts legend payload
   */
  const handleLegendClick = ({ dataKey }) => {
    const isDataTypeDisabled = disabledStations.includes(dataKey)
    if (isDataTypeDisabled) {
      setDisabledStations(disabledStations.filter(value => value !== dataKey))
    } else {
      setDisabledStations(disabledStations.concat(dataKey))
    }
  }

  return (
    <ResponsiveContainer width="100%" aspect={props.aspect || 4.0 / 3.0}>
      <BarChart width={props.width} height={props.height} data={props.data} margin={chartMargins}>
        <XAxis dataKey="date" interval={0} angle={-45} height={80} textAnchor="end">
          <Label value={props.xAxisLabel} position="insideBottom" offset={-3} />
        </XAxis>
        <CartesianGrid stroke="#f5f5f5" />
        <YAxis type="number" domain={[0, 100]} tickFormatter={percentageTickFormatter}>
          <Label value={props.yAxisLabel} position="insideLeft" angle={-90} style={{ textAnchor: 'middle' }} />
        </YAxis>

        <Tooltip content={<OccupancyTooltip />} />
        <Legend
          verticalAlign="top"
          wrapperStyle={legendStyling}
          payload={props.itemList.map(item => ({
            dataKey: item,
            //grey out legend colour if data type is disabled
            color: disabledStations.includes(item) ? colors.grey : props.itemColors[item],
            value: item
          }))}
          content={<CustomLegend onLegendClick={handleLegendClick} />}
        />
        <pattern id="pattern-stripe" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="2" height="4" transform="translate(0,0)" fill={colors.white} />
        </pattern>

        <mask id="mask-stripe">
          <rect x="0" y="0" width="100%" height="100%" fill="url(#pattern-stripe)" />
        </mask>

        {props.itemList
          .filter(item => !disabledStations.includes(item))
          .map(item => {
            const itemColor = props.itemColors[item] ? props.itemColors[item].toUpperCase() : ''
            return [
              <Bar
                stackId={item}
                key={`${item}${occupancyDataKeySuffix.workingHours}`}
                dataKey={`${item}${occupancyDataKeySuffix.workingHours}`}
                fill={props.itemColors[item]}
                name={item}
                isAnimationActive={false}
                unit="%"
                // bars should only have a border when product color is white
                stroke={colors.white === itemColor ? colors.grey : ''}
                strokeWidth={colors.white === itemColor ? 3 : ''}
              />,
              <Bar
                stackId={item}
                name={item}
                key={`${item}${occupancyDataKeySuffix.nonWorkingHours}`}
                isAnimationActive={false}
                dataKey={`${item}${occupancyDataKeySuffix.nonWorkingHours}`}
                mask="url(#mask-stripe)"
                fill={props.itemColors[item]}
                unit="%"
                // bars should only have a border when product color is white
                stroke={colors.white === itemColor ? colors.grey : ''}
                strokeWidth={colors.white === itemColor ? 3 : ''}
              />
            ]
          })}
      </BarChart>
    </ResponsiveContainer>
  )
}

OccupancyBarChart.propTypes = {
  itemList: PropTypes.array.isRequired,
  itemColors: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  xAxisLabel: PropTypes.string,
  yAxisLabel: PropTypes.string,
  aspect: PropTypes.number
}
export default OccupancyBarChart
